import { Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import type { DownloadProgress, DownloadStructure } from '@/types/download';
import type { DownloadMode } from './downloadWorkflow';
import { cn } from '@/utils/cn';
import { getDownloadStructurePreviewPath } from '@/utils/downloadStructure';

interface SelectionSummaryProps {
  totalActivities: number;
  totalPatients: number;
  mode: DownloadMode;
  downloadStructure: DownloadStructure;
  onDownloadStructureChange: (value: DownloadStructure) => void;
  onDownload: () => void;
  isDownloading: boolean;
  progress?: DownloadProgress;
}

export function SelectionSummary({
  totalActivities,
  totalPatients,
  mode,
  downloadStructure,
  onDownloadStructureChange,
  onDownload,
  isDownloading,
  progress,
}: SelectionSummaryProps) {
  const canDownload = totalActivities > 0;

  const getProgressText = () => {
    if (!progress) return null;

    switch (progress.status) {
      case 'fetching':
        return `Fetching activities... ${progress.current}/${progress.total}`;
      case 'packaging':
        return 'Creating ZIP file...';
      case 'complete':
        return 'Download complete!';
      case 'error':
        return progress.message || 'An error occurred';
      default:
        return null;
    }
  };

  const progressText = getProgressText();
  const progressPercentage = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <Card>
      <CardBody className="flex flex-col gap-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="text-center lg:text-left">
            <p className="text-lg font-semibold text-foreground">
              {totalActivities.toLocaleString()} {totalActivities === 1 ? 'activity' : 'activities'} selected
              {' '}from {totalPatients} {totalPatients === 1 ? 'patient' : 'patients'}
            </p>
            {progressText && (
              <div className="mt-2">
                <p className="text-sm text-muted">{progressText}</p>
                {progress?.status === 'fetching' && (
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 lg:min-w-[24rem]">
            <div className="border-b border-border pb-4 lg:border-b-0 lg:pb-0">
              <div className="flex flex-col gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Download structure</p>
                  <p className="mt-1 text-sm text-muted">
                    Example: <span className="font-mono text-xs text-foreground">{getDownloadStructurePreviewPath(downloadStructure, mode)}</span>
                  </p>
                </div>

                <div className="inline-flex w-full rounded-lg border border-border bg-gray-50 p-1">
                  {[
                    { value: 'flat', label: 'Flat files' },
                    { value: 'folders', label: 'Folder per activity' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onDownloadStructureChange(option.value as DownloadStructure)}
                      className={cn(
                        'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        downloadStructure === option.value
                          ? 'bg-white text-foreground shadow-sm'
                          : 'text-muted hover:text-foreground'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
            {progress?.status === 'error' && (
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Failed</span>
              </div>
            )}
            <Button
              size="lg"
              onClick={onDownload}
              isLoading={isDownloading}
              disabled={!canDownload || isDownloading}
              leftIcon={<Download className="h-5 w-5" />}
            >
              Download ZIP
            </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
